import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { mapSaga } from './saga';
import {
  ILoadPolygonsPayload,
  IPolygon,
  ITempPolygon,
  MapModeEnum,
  MapState,
} from './types';
import { LatLngTuple } from 'leaflet';
import {
  IFieldProgress,
  ISetFieldProgressPayload,
} from 'app/containers/Task/slice/types';
import { ITempFieldResponse } from 'app/containers/Field/slice/types';
import { MAP_LAYERS } from './map-constants';
import { getCultureFieldIds, getVariationFieldIds } from './helpers';

export const initialState: MapState = {
  isMapReady: false,
  polygons: [],
  activePolygonId: null,
  fieldsCultureVariations: {},
  singleFieldOnMap: {
    fieldId: null,
  },

  uploadFields: {
    tempPolygons: [],
    selectedIds: [],
  },
  mapMode: MapModeEnum.view,
  selectedPolygons: [],
  loading: false,
  cultureMenuSelectedIds: [],
  cultureMenuSelectedVariationsIds: [],
  layers: MAP_LAYERS.LAYERS,

  fitCoordArrOnMap: null,
  fieldsProgress: {
    fieldsProgressArr: null,
    updatedFieldsArr: null,
  },

  transport: {
    hiddenOnMapGpsVehicleIds: [],
    highlightedRoutesGpsVehicleId: null,
  },

  showCurrentUserLocation: false,

  visibleRoutesBySelectedTaskFieldSeasonId: null,
};

const slice = createSlice({
  name: 'map',
  initialState,
  reducers: {
    setVisibleRoutesBySelectedTaskFieldSeasonId(
      state,
      action: PayloadAction<number | null>,
    ) {
      state.visibleRoutesBySelectedTaskFieldSeasonId = action.payload;
    },

    setShowCurrentUserLocation(state, action: PayloadAction<boolean>) {
      state.showCurrentUserLocation = action.payload;
    },

    setIsMapReady(state, action: PayloadAction<boolean>) {
      state.isMapReady = action.payload;
    },

    setHighlightedRoutesGpsVehicleId(
      state,
      action: PayloadAction<number | null>,
    ) {
      state.transport.highlightedRoutesGpsVehicleId = action.payload;
    },

    setHiddenOnMapGpsVehicleIds(state, action: PayloadAction<number[]>) {
      state.transport.hiddenOnMapGpsVehicleIds = action.payload;
    },

    toggleHiddenOnMapByGpsVehicleId(state, action: PayloadAction<number>) {
      const transportId = action.payload;
      if (state.transport.hiddenOnMapGpsVehicleIds.includes(transportId)) {
        state.transport.hiddenOnMapGpsVehicleIds = state.transport.hiddenOnMapGpsVehicleIds.filter(
          id => id !== transportId,
        );
      } else {
        state.transport.hiddenOnMapGpsVehicleIds.push(transportId);
      }
    },

    reorderLayers(
      state,
      action: PayloadAction<{
        startIndex: number;
        endIndex: number;
      }>,
    ) {
      const { startIndex, endIndex } = action.payload;
      const result = Array.from(state.layers);
      const [removed] = result.splice(startIndex, 1);
      result.splice(endIndex, 0, removed);
      state.layers = result;
    },

    setLayerOpacity(
      state,
      action: PayloadAction<{ layerId: string; opacity: number }>,
    ) {
      const { layerId, opacity } = action.payload;

      state.layers = state.layers.map(layer => {
        if (layer.id === layerId) {
          return { ...layer, opacity };
        }
        return layer;
      });
    },

    addSelectedFieldsByCultureId(state, action: PayloadAction<number>) {
      const cultureId = action.payload || null;
      const cultureFieldIds = getCultureFieldIds(cultureId, state.polygons);
      state.selectedPolygons = Array.from(
        new Set([...state.selectedPolygons, ...cultureFieldIds]),
      );
    },

    removeSelectedFieldsByCultureId(state, action: PayloadAction<number>) {
      const cultureId = action.payload || null;
      const cultureFieldIds = getCultureFieldIds(cultureId, state.polygons);
      state.selectedPolygons = state.selectedPolygons.filter(
        selectedId => !cultureFieldIds.includes(selectedId),
      );
    },

    addSelectedFieldsByVariationId(state, action: PayloadAction<number>) {
      const variationId = action.payload;
      const variationFieldIds = getVariationFieldIds(
        variationId,
        state.polygons,
      );
      state.selectedPolygons = Array.from(
        new Set([...state.selectedPolygons, ...variationFieldIds]),
      );
    },

    removeSelectedFieldsByVariationId(state, action: PayloadAction<number>) {
      const variationId = action.payload;
      const variationFieldIds = getVariationFieldIds(
        variationId,
        state.polygons,
      );
      state.selectedPolygons = state.selectedPolygons.filter(
        selectedId => !variationFieldIds.includes(selectedId),
      );
    },

    toggleActivePolygonId(state, action: PayloadAction<number | null>) {
      state.activePolygonId =
        state.activePolygonId === action.payload ? null : action.payload;
    },

    setMapLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },

    setMapMode(state, action: PayloadAction<MapModeEnum>) {
      state.mapMode = action.payload;
    },

    setSingleFieldOnMapFieldId(state, action: PayloadAction<null | number>) {
      state.singleFieldOnMap.fieldId = action.payload;
    },

    onLoad: (state, action: PayloadAction<ILoadPolygonsPayload>) => {
      state.loading = true;
      state.selectedPolygons = [];
      state.polygons = [];
    },
    reloadPolygons: (state, action: PayloadAction<ILoadPolygonsPayload>) => {},

    setPolygons: (state, action: PayloadAction<IPolygon[]>) => {
      state.polygons = action.payload;
    },

    togglePolygon(state, action: PayloadAction<{ fieldId: number }>) {
      const { fieldId } = action.payload;
      const isSelected = state.selectedPolygons.includes(fieldId);
      if (isSelected) {
        state.selectedPolygons = state.selectedPolygons.filter(
          sp => sp !== fieldId,
        );
      } else {
        state.selectedPolygons.push(fieldId);
      }
    },
    zoomOnPolygon(state, action: PayloadAction<number>) {},

    setTempPolygons: (state, action: PayloadAction<ITempPolygon[]>) => {
      state.uploadFields.tempPolygons = action.payload;
    },

    toggleTempPolygon(state, action: PayloadAction<string>) {
      const id = action.payload;
      const isSelected = state.uploadFields.selectedIds.includes(id);
      if (isSelected) {
        state.uploadFields.selectedIds = state.uploadFields.selectedIds.filter(
          polygonId => polygonId !== id,
        );
      } else {
        state.uploadFields.selectedIds.push(id);
      }
    },

    toggleAllTempPolygon(state) {
      const isAllSelected =
        state.uploadFields.selectedIds.length ===
        state.uploadFields.tempPolygons.length;

      if (isAllSelected) {
        state.uploadFields.selectedIds = [];
      } else {
        state.uploadFields.selectedIds = state.uploadFields.tempPolygons.map(
          polygon => polygon.id,
        );
      }
    },

    clearUploadFieldsState(state) {
      state.uploadFields = initialState.uploadFields;
    },

    zoomOnTempPolygon(state, action: PayloadAction<string>) {},
    zoomOnDrawnPolygon(state, action: PayloadAction<string>) {},

    toggleLayer(state, action: PayloadAction<{ layerId: string }>) {
      const layer = state.layers.find(l => l.id === action.payload.layerId);
      if (layer) {
        layer.visible = !layer.visible;
      }
    },

    setSelectedPolygonsIds(state, action: PayloadAction<number[]>) {
      state.selectedPolygons = action.payload;
    },

    setSingleFieldProgress(
      state,
      action: PayloadAction<ISetFieldProgressPayload>,
    ) {
      const { fieldId, progress } = action.payload;
      state.fieldsProgress.updatedFieldsArr =
        state.fieldsProgress.updatedFieldsArr?.map(field => {
          if (field.fieldId === fieldId) {
            return { ...field, progress };
          }
          return field;
        }) || null;
    },
    setUpdatedFieldsProgressArr(
      state,
      action: PayloadAction<IFieldProgress[]>,
    ) {
      state.fieldsProgress.updatedFieldsArr = action.payload;
    },
    setFieldsProgress(state, action: PayloadAction<IFieldProgress[]>) {
      state.fieldsProgress.fieldsProgressArr = action.payload;
      state.fieldsProgress.updatedFieldsArr = action.payload;
    },

    setFitCoordArrOnMap(
      state,
      action: PayloadAction<LatLngTuple[] | L.LatLng[] | null>,
    ) {
      state.fitCoordArrOnMap = action.payload;
    },

    fitAllPolygonsOnMap(state, action: PayloadAction) {},

    zoomSingleFieldMap(state, action: PayloadAction) {},

    getTempFieldsFromTempFieldResponse(
      state,
      action: PayloadAction<ITempFieldResponse>,
    ) {},

    setSelectedCultureMenuVariationsIds(
      state,
      action: PayloadAction<number[]>,
    ) {
      state.cultureMenuSelectedVariationsIds = action.payload;
    },
    toggleCultureMenuSelectedVariationId(state, action: PayloadAction<number>) {
      const id = action.payload;
      const isInArr = state.cultureMenuSelectedVariationsIds.includes(id);

      if (isInArr) {
        state.cultureMenuSelectedVariationsIds = state.cultureMenuSelectedVariationsIds.filter(
          variationId => variationId !== id,
        );
      } else {
        state.cultureMenuSelectedVariationsIds.push(id);
      }
    },

    setSelectedCultureMenuIds(state, action: PayloadAction<number[]>) {
      state.cultureMenuSelectedIds = action.payload;
    },
    toggleCultureMenuSelectedId(state, action: PayloadAction<number>) {
      const id = action.payload;
      const isInArr = state.cultureMenuSelectedIds.includes(id);

      if (isInArr) {
        state.cultureMenuSelectedIds = state.cultureMenuSelectedIds.filter(
          cultureId => cultureId !== id,
        );
      } else {
        state.cultureMenuSelectedIds.push(id);
      }
    },
  },
});

export const { actions: mapActions } = slice;

export const useMapSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectSaga({ key: slice.name, saga: mapSaga });
  return { actions: slice.actions };
};

/**
 * Example Usage:
 *
 * export function MyComponentNeedingThisSlice() {
 *  const { actions } = useMapSlice();
 *
 *  const onButtonClick = (evt) => {
 *    dispatch(actions.someAction());
 *   };
 * }
 */
